import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import type {
  ActivePatientCensusData,
  CensusByPrimaryDiagnosisData,
  CensusByPrimaryInsurerData,
  OrdersPendingSignatureData,
  OutstandingOasisData,
  TaskQueueData,
  DashboardQueryResponse,
  DashboardWidgetError,
} from '../../services/api/types.gen';
import { DASHBOARD_QUERIES, type DashboardQuery } from './dashboardConfig';
import { fetchDashboard } from './dashboardApi';
import type {
  WidgetItemLimitPreferences,
  WidgetViewPreferences,
} from './useWidgetDisplayPreferences';

type RequestMode = 'load' | 'refresh';
type DashboardResult<Q extends DashboardQuery> = NonNullable<
  DashboardQueryResponse[Q]
>;
type DashboardApiSuccess<Q extends DashboardQuery> = Extract<
  DashboardResult<Q>,
  { status: 'success' }
>;
type DashboardApiError<Q extends DashboardQuery = DashboardQuery> = Extract<
  DashboardResult<Q>,
  { status: 'error' }
>;
type WidgetGenerationMap = Record<DashboardQuery, number>;
type RequestGenerations = Partial<WidgetGenerationMap>;

type TaskCategoryViewData = {
  id: string;
  label: string;
  count: number;
};

type MetricViewData = TaskCategoryViewData & {
  unit: string;
};

type RankedBreakdownItemViewData = TaskCategoryViewData & {
  percentage: number;
  rank: number;
};

type OrderAgeBucketViewData = TaskCategoryViewData & {
  minimumAgeDays: number | null;
  maximumAgeDays: number | null;
  percentage: number;
};

type CountWidgetData<K extends DashboardQuery> = {
  kind: K;
  count: number;
  unit: string;
};

export type ActivePatientCensusWidgetData =
  CountWidgetData<'active_patient_census'> & {
    active_home_count?: number;
    hospital_hold_count?: number;
  };

type TaskQueueWidgetData<K extends DashboardQuery> = {
  kind: K;
  total: number;
  unit: string;
  categories: TaskCategoryViewData[];
};

type BreakdownWidgetData<K extends DashboardQuery> = {
  kind: K;
  denominator: number;
  unit: string;
  items: RankedBreakdownItemViewData[];
};

export type DashboardWidgetDataByKind = {
  active_patient_census: ActivePatientCensusWidgetData;
  case_manager_queue: TaskQueueWidgetData<'case_manager_queue'>;
  past_due_tasks: TaskQueueWidgetData<'past_due_tasks'>;
  outstanding_oasis: {
    kind: 'outstanding_oasis';
    metrics: MetricViewData[];
  };
  census_by_primary_insurer: BreakdownWidgetData<'census_by_primary_insurer'>;
  census_by_primary_diagnosis: BreakdownWidgetData<'census_by_primary_diagnosis'>;
  orders_pending_signature: {
    kind: 'orders_pending_signature';
    total: number;
    unit: string;
    buckets: OrderAgeBucketViewData[];
  };
};

export type DashboardWidgetData =
  DashboardWidgetDataByKind[keyof DashboardWidgetDataByKind];

type DashboardApiDataByQuery = {
  [Query in DashboardQuery]: DashboardApiSuccess<Query>['data'];
};

export type DashboardWidgetState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'refreshing'; timestamp: string; data: DashboardWidgetData }
  | { status: 'success'; timestamp: string; data: DashboardWidgetData }
  | { status: 'empty'; timestamp: string; data: DashboardWidgetData }
  | { status: 'error'; timestamp: string; error: DashboardWidgetError }
  | {
      status: 'stale_error';
      timestamp: string;
      data: DashboardWidgetData;
      staleError: DashboardWidgetError;
    };

type DashboardWidgetStates = Record<DashboardQuery, DashboardWidgetState>;

const REQUEST_ERROR_MESSAGE = 'Unable to load dashboard. Please try again.';
const REFRESH_ERROR_MESSAGE = 'Unable to refresh dashboard. Please try again.';
const REFRESH_STALE_ERROR: DashboardWidgetError = {
  code: 'temporarily_unavailable',
  message: REFRESH_ERROR_MESSAGE,
  retryable: true,
};

const widgetDataAdapters = {
  active_patient_census: (
    data: ActivePatientCensusData
  ): DashboardWidgetDataByKind['active_patient_census'] => ({
    kind: 'active_patient_census',
    count: data.count,
    active_home_count: data.active_home_count,
    hospital_hold_count: data.hospital_hold_count,
    unit: data.unit ?? 'patients',
  }),
  case_manager_queue: (
    data: TaskQueueData
  ): DashboardWidgetDataByKind['case_manager_queue'] =>
    taskQueueData('case_manager_queue', data),
  past_due_tasks: (
    data: TaskQueueData
  ): DashboardWidgetDataByKind['past_due_tasks'] =>
    taskQueueData('past_due_tasks', data),
  outstanding_oasis: (
    data: OutstandingOasisData
  ): DashboardWidgetDataByKind['outstanding_oasis'] => ({
    kind: 'outstanding_oasis',
    metrics: [data.assigned_not_completed, data.completed_not_exported].map(
      metric => ({
        id: metric.id,
        label: metric.label,
        count: metric.count,
        unit: metric.unit ?? 'assessments',
      })
    ),
  }),
  census_by_primary_insurer: (
    data: CensusByPrimaryInsurerData
  ): DashboardWidgetDataByKind['census_by_primary_insurer'] => ({
    kind: 'census_by_primary_insurer',
    denominator: data.denominator_count,
    unit: data.unit ?? 'patients',
    items: data.series.map(item => ({
      id: item.id,
      label: item.label,
      count: item.count,
      percentage: item.percentage,
      rank: item.rank,
    })),
  }),
  census_by_primary_diagnosis: (
    data: CensusByPrimaryDiagnosisData
  ): DashboardWidgetDataByKind['census_by_primary_diagnosis'] => ({
    kind: 'census_by_primary_diagnosis',
    denominator: data.denominator_count,
    unit: data.unit ?? 'patients',
    items: data.items.map(item => ({
      id: item.category_id,
      label: item.code ? `${item.code} — ${item.label}` : item.label,
      count: item.count,
      percentage: item.percentage,
      rank: item.rank,
    })),
  }),
  orders_pending_signature: (
    data: OrdersPendingSignatureData
  ): DashboardWidgetDataByKind['orders_pending_signature'] => ({
    kind: 'orders_pending_signature',
    total: data.total,
    unit: data.unit ?? 'orders',
    buckets: data.buckets.map(bucket => ({
      id: bucket.id,
      label: bucket.label,
      minimumAgeDays: bucket.minimum_age_days ?? null,
      maximumAgeDays: bucket.maximum_age_days ?? null,
      count: bucket.count,
      percentage: bucket.percentage,
    })),
  }),
} satisfies {
  [Query in DashboardQuery]: (
    data: DashboardApiDataByQuery[Query]
  ) => DashboardWidgetDataByKind[Query];
};

function initialWidgetStates(): DashboardWidgetStates {
  return Object.fromEntries(
    DASHBOARD_QUERIES.map(query => [query, { status: 'loading' }])
  ) as DashboardWidgetStates;
}

function initialWidgetGenerations(): WidgetGenerationMap {
  return Object.fromEntries(
    DASHBOARD_QUERIES.map(query => [query, 0])
  ) as WidgetGenerationMap;
}

function isSuccess<Q extends DashboardQuery>(
  result: DashboardResult<Q>
): result is DashboardApiSuccess<Q> {
  return result.status === 'success';
}

function taskQueueData<K extends 'case_manager_queue' | 'past_due_tasks'>(
  kind: K,
  data: TaskQueueData
): TaskQueueWidgetData<K> {
  return {
    kind,
    total: data.total,
    unit: data.unit ?? 'tasks',
    categories: data.categories.map(category => ({
      id: category.id,
      label: category.label,
      count: category.count,
    })),
  };
}

function toWidgetData<Q extends DashboardQuery>(
  query: Q,
  result: DashboardApiSuccess<Q>
): DashboardWidgetDataByKind[Q] {
  const adapt = widgetDataAdapters[query] as unknown as (
    data: DashboardApiDataByQuery[Q]
  ) => DashboardWidgetDataByKind[Q];
  return adapt(result.data as DashboardApiDataByQuery[Q]);
}

function isZeroData(data: DashboardWidgetData): boolean {
  switch (data.kind) {
    case 'active_patient_census':
      return data.count === 0;
    case 'case_manager_queue':
    case 'past_due_tasks':
      return data.total === 0;
    case 'outstanding_oasis':
      return data.metrics.every(metric => metric.count === 0);
    case 'census_by_primary_insurer':
    case 'census_by_primary_diagnosis':
      return data.denominator === 0;
    case 'orders_pending_signature':
      return data.total === 0;
  }
}

function successState<Q extends DashboardQuery>(
  query: Q,
  result: DashboardApiSuccess<Q>
): DashboardWidgetState {
  const data = toWidgetData(query, result);
  return {
    status: isZeroData(data) ? 'empty' : 'success',
    timestamp: result.timestamp,
    data,
  };
}

function errorState(
  previous: DashboardWidgetState,
  result: DashboardApiError
): DashboardWidgetState {
  // A failed refresh should not blank out a widget clinicians were already
  // using; show the last good answer and mark it stale instead.
  if (hasUsableData(previous)) {
    return {
      status: 'stale_error',
      timestamp: previous.timestamp,
      data: previous.data,
      staleError: result.error,
    };
  }

  return {
    status: 'error',
    timestamp: result.timestamp,
    error: result.error,
  };
}

export function hasUsableData(
  state: DashboardWidgetState
): state is Extract<
  DashboardWidgetState,
  { status: 'success' | 'empty' | 'refreshing' | 'stale_error' }
> {
  return (
    state.status === 'success' ||
    state.status === 'empty' ||
    state.status === 'refreshing' ||
    state.status === 'stale_error'
  );
}

function pendingWidgetState(
  previous: DashboardWidgetState,
  mode: RequestMode
): DashboardWidgetState {
  if (mode === 'refresh' && hasUsableData(previous)) {
    return {
      status: 'refreshing',
      timestamp: previous.timestamp,
      data: previous.data,
    };
  }

  return { status: 'loading' };
}

function markWidgetsPending(
  previous: DashboardWidgetStates,
  queries: DashboardQuery[],
  mode: RequestMode
): DashboardWidgetStates {
  const next = { ...previous };
  for (const query of queries) {
    next[query] = pendingWidgetState(previous[query], mode);
  }
  return next;
}

function applyDashboardResponse({
  previous,
  queries,
  response,
  requestScopeKey,
  currentScopeKey,
  requestGenerations,
  currentGenerations,
}: {
  previous: DashboardWidgetStates;
  queries: DashboardQuery[];
  response: DashboardQueryResponse;
  requestScopeKey: string;
  currentScopeKey: string;
  requestGenerations: RequestGenerations;
  currentGenerations: WidgetGenerationMap;
}): DashboardWidgetStates {
  const next = { ...previous };
  for (const query of queries) {
    if (
      currentScopeKey !== requestScopeKey ||
      currentGenerations[query] !== requestGenerations[query]
    ) {
      continue;
    }

    const result = response[query];
    if (!result) continue;
    next[query] = isSuccess(result)
      ? successState(query, result)
      : errorState(previous[query], result);
  }
  return next;
}

function recoverRejectedRequest(
  previous: DashboardWidgetStates,
  queries: DashboardQuery[],
  requestGenerations: RequestGenerations,
  currentGenerations: WidgetGenerationMap
): DashboardWidgetStates {
  const next = { ...previous };
  for (const query of queries) {
    if (currentGenerations[query] !== requestGenerations[query]) {
      continue;
    }

    const previousWidget = previous[query];
    if (previousWidget.status === 'loading') {
      next[query] = { status: 'idle' };
    } else if (previousWidget.status === 'refreshing') {
      next[query] = {
        status: 'stale_error',
        timestamp: previousWidget.timestamp,
        data: previousWidget.data,
        staleError: REFRESH_STALE_ERROR,
      };
    }
  }
  return next;
}

function beginWidgetGenerations(
  queries: DashboardQuery[],
  generations: WidgetGenerationMap
): RequestGenerations {
  const requestGenerations: RequestGenerations = {};
  for (const query of queries) {
    generations[query] += 1;
    requestGenerations[query] = generations[query];
  }
  return requestGenerations;
}

function hasActiveRequestQuery(
  queries: DashboardQuery[],
  requestScopeKey: string,
  currentScopeKey: string,
  requestGenerations: RequestGenerations,
  currentGenerations: WidgetGenerationMap
): boolean {
  return queries.some(
    query =>
      currentScopeKey === requestScopeKey &&
      currentGenerations[query] === requestGenerations[query]
  );
}

function trackAbortController(
  queries: DashboardQuery[],
  abortControllers: Partial<Record<DashboardQuery, AbortController>>,
  abortController: AbortController
) {
  for (const query of queries) {
    abortControllers[query] = abortController;
  }
}

function clearAbortController(
  queries: DashboardQuery[],
  abortControllers: Partial<Record<DashboardQuery, AbortController>>,
  abortController: AbortController
) {
  for (const query of queries) {
    if (abortControllers[query] === abortController) {
      delete abortControllers[query];
    }
  }
}

function abortPendingRequests(
  abortControllers: Partial<Record<DashboardQuery, AbortController>>
) {
  for (const controller of new Set(Object.values(abortControllers))) {
    controller?.abort();
  }
}

function normalizeClinicIds(clinicIds: string[]): string[] {
  return [...new Set(clinicIds)].sort();
}

function scopeKey({
  userId,
  agencyId,
  clinicIds,
  includeImportedPatients = false,
  includeTestPatients = false,
}: {
  userId: string | null;
  agencyId: string | null;
  clinicIds: string[];
  includeImportedPatients?: boolean;
  includeTestPatients?: boolean;
}): string {
  const normalized = normalizeClinicIds(clinicIds);
  const clinicScope = normalized.length === 0 ? 'all' : normalized.join('|');
  return `${userId ?? 'anonymous'}:${agencyId ?? 'no-agency'}:${clinicScope}:imp-${includeImportedPatients}:test-${includeTestPatients}`;
}

function isAbortLikeError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.name === 'CanceledError' || error.name === 'AbortError';
}

function shouldRefreshBackendLimitedWidget(
  query: 'census_by_primary_insurer' | 'census_by_primary_diagnosis',
  preferences: WidgetViewPreferences,
  previousPreferences: WidgetViewPreferences,
  itemLimits: WidgetItemLimitPreferences,
  previousItemLimits: WidgetItemLimitPreferences
): boolean {
  const selectedView = preferences[query];
  return (
    selectedView !== previousPreferences[query] ||
    itemLimits[query][selectedView] !== previousItemLimits[query][selectedView]
  );
}

function backendLimitedPreferenceRefreshQueries(
  preferences: WidgetViewPreferences,
  previousPreferences: WidgetViewPreferences,
  itemLimits: WidgetItemLimitPreferences,
  previousItemLimits: WidgetItemLimitPreferences
): DashboardQuery[] {
  const queries: DashboardQuery[] = [];

  // Only grouped census widgets need a server refetch when the view or active
  // limit changes; other preference changes are handled while rendering.
  for (const query of [
    'census_by_primary_insurer',
    'census_by_primary_diagnosis',
  ] as const) {
    if (
      shouldRefreshBackendLimitedWidget(
        query,
        preferences,
        previousPreferences,
        itemLimits,
        previousItemLimits
      )
    ) {
      queries.push(query);
    }
  }

  return queries;
}

export function useDashboardQuery({
  userId = null,
  agencyId = null,
  clinicIds,
  preferences,
  itemLimits,
  includeImportedPatients = false,
  includeTestPatients = false,
  enabled = true,
}: {
  userId?: string | null;
  agencyId?: string | null;
  clinicIds: string[];
  preferences: WidgetViewPreferences;
  itemLimits: WidgetItemLimitPreferences;
  includeImportedPatients?: boolean;
  includeTestPatients?: boolean;
  enabled?: boolean;
}) {
  const rawClinicIdsKey = clinicIds.join('|');
  const normalizedClinicIds = useMemo(
    () => normalizeClinicIds(clinicIds),
    [rawClinicIdsKey]
  );
  const currentScopeKey = useMemo(
    () =>
      scopeKey({
        userId,
        agencyId,
        clinicIds: normalizedClinicIds,
        includeImportedPatients,
        includeTestPatients,
      }),
    [
      agencyId,
      includeImportedPatients,
      includeTestPatients,
      normalizedClinicIds,
      userId,
    ]
  );
  const [widgets, setWidgets] = useState<DashboardWidgetStates>(() =>
    initialWidgetStates()
  );
  const [requestError, setRequestError] = useState<string | null>(null);
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;
  const previousPreferencesRef = useRef(preferences);
  const itemLimitsRef = useRef(itemLimits);
  itemLimitsRef.current = itemLimits;
  const previousItemLimitsRef = useRef(itemLimits);
  const generations = useRef<WidgetGenerationMap>(initialWidgetGenerations());
  const scopeRef = useRef(currentScopeKey);
  const abortControllers = useRef<
    Partial<Record<DashboardQuery, AbortController>>
  >({});

  const runRequest = useCallback(
    async (queries: DashboardQuery[], mode: RequestMode) => {
      if (!enabled) {
        abortPendingRequests(abortControllers.current);
        abortControllers.current = {};
        setRequestError(null);
        setWidgets(initialWidgetStates());
        return;
      }
      setRequestError(null);
      if (mode === 'load') {
        abortPendingRequests(abortControllers.current);
        abortControllers.current = {};
      }

      const requestScopeKey = currentScopeKey;
      // Each widget has its own generation so a slow response can update only
      // the widgets it still owns after filters or preferences change.
      const requestGenerations = beginWidgetGenerations(
        queries,
        generations.current
      );
      const abortController = new AbortController();
      trackAbortController(queries, abortControllers.current, abortController);

      setWidgets(prev => markWidgetsPending(prev, queries, mode));

      try {
        const response = await fetchDashboard({
          queries,
          clinicIds: normalizedClinicIds,
          viewPreferences: preferencesRef.current,
          itemLimits: itemLimitsRef.current,
          include_imported_patients: includeImportedPatients,
          include_test_patients: includeTestPatients,
          signal: abortController.signal,
        });

        setWidgets(prev =>
          applyDashboardResponse({
            previous: prev,
            queries,
            response,
            requestScopeKey,
            currentScopeKey: scopeRef.current,
            requestGenerations,
            currentGenerations: generations.current,
          })
        );
      } catch (error) {
        const hasActiveQuery = hasActiveRequestQuery(
          queries,
          requestScopeKey,
          scopeRef.current,
          requestGenerations,
          generations.current
        );
        if (!hasActiveQuery || isAbortLikeError(error)) return;

        console.error('Failed to load dashboard:', error);
        setRequestError(
          getBusinessErrorMessage(error) ?? REQUEST_ERROR_MESSAGE
        );
        setWidgets(prev =>
          recoverRejectedRequest(
            prev,
            queries,
            requestGenerations,
            generations.current
          )
        );
      } finally {
        clearAbortController(
          queries,
          abortControllers.current,
          abortController
        );
      }
    },
    [
      currentScopeKey,
      enabled,
      includeImportedPatients,
      includeTestPatients,
      normalizedClinicIds,
    ]
  );

  useEffect(() => {
    // Clinic access resolves separately from widget data. Hold widget requests
    // until that scope is known so an empty or stale selection cannot load data.
    scopeRef.current = currentScopeKey;
    previousPreferencesRef.current = preferencesRef.current;
    previousItemLimitsRef.current = itemLimitsRef.current;
    setWidgets(initialWidgetStates());
    if (enabled) {
      void runRequest([...DASHBOARD_QUERIES], 'load');
    } else {
      abortPendingRequests(abortControllers.current);
      abortControllers.current = {};
      setRequestError(null);
      setWidgets(initialWidgetStates());
    }
  }, [currentScopeKey, enabled, runRequest]);

  useEffect(() => {
    const previousPreferences = previousPreferencesRef.current;
    const previousItemLimits = previousItemLimitsRef.current;
    const queriesToRefresh = backendLimitedPreferenceRefreshQueries(
      preferences,
      previousPreferences,
      itemLimits,
      previousItemLimits
    );

    previousPreferencesRef.current = preferences;
    previousItemLimitsRef.current = itemLimits;

    if (queriesToRefresh.length > 0) {
      void runRequest(queriesToRefresh, 'refresh');
    }
  }, [itemLimits, preferences, runRequest]);

  useEffect(
    () => () => {
      abortPendingRequests(abortControllers.current);
    },
    []
  );

  const refreshWidget = useCallback(
    (query: DashboardQuery) => runRequest([query], 'refresh'),
    [runRequest]
  );
  const refreshAll = useCallback(
    () => runRequest([...DASHBOARD_QUERIES], 'refresh'),
    [runRequest]
  );

  // Effects run after render. Hide the previous scope's values during that
  // transition so changing clinic filters cannot briefly show stale widgets.
  const scopeChanged = scopeRef.current !== currentScopeKey;

  return {
    widgets: scopeChanged ? initialWidgetStates() : widgets,
    requestError: scopeChanged ? null : requestError,
    refreshWidget,
    refreshAll,
  };
}
